import { create } from 'zustand';
import { toast } from 'react-toastify';
import { t } from 'i18next';
import { IBathPress, ICustomer, IMaturityConcrete } from '@/models';
import { http } from '@/utils';
import { API_ENDPOINTS, LoadingKeys } from '@/constants';
import { handleApiCallInStore } from './utils';

export interface IMaturityState {
  customers: ICustomer[];
  concretes: IMaturityConcrete[];
  selectedConcrete: IMaturityConcrete | null;
  getCustomers: () => void;
  getConcretes: (customerId: number) => Promise<void>;
  addConcrete: (name: string, comment: string | null, customerId: number, onSuccess: () => void) => void;
  addConcretePress: (
    payload: IBathPress & { concrete_id: number },
    customerId: number,
    onSuccess: () => void
  ) => void;
  calcMaturityParams: (concreteId: number, customerId: number, onSuccess: () => void) => void;
  setSelectedConcrete: (concrete: IMaturityConcrete) => void;
}

export const useMaturityStore = create<IMaturityState>((set, get) => ({
  customers: [],
  concretes: [],
  selectedConcrete: null,

  getCustomers: () =>
    handleApiCallInStore(LoadingKeys.GetCustomers, async () => {
      const { data } = await http.get<ICustomer[]>(API_ENDPOINTS.customers.getCustomers);
      set({ customers: data });
    }),

  getConcretes: (customerId) =>
    handleApiCallInStore(LoadingKeys.GetMaturityConcretes, async () => {
      const { data } = await http.get<IMaturityConcrete[]>(
        API_ENDPOINTS.maturity.concretes(customerId)
      );
      set({ concretes: data });
    }),

  addConcrete: (name, comment, customerId, onSuccess) =>
    handleApiCallInStore(LoadingKeys.AddMaturityConcrete, async () => {
      const { data } = await http.post<[[IMaturityConcrete], number]>(
        API_ENDPOINTS.maturity.addConcrete,
        { name, comment: comment || null },
        { customerContextOverride: customerId } as any
      );
      const insertedConcrete = data?.[0]?.[0];

      if (!insertedConcrete || insertedConcrete.concrete_id == null) {
        toast.error(t('serverError'));
        return;
      }

      set({
        concretes: [...get().concretes, { ...insertedConcrete, concrete_bath_presses: [] }],
      });
      toast.success(t('concreteAddedSuccessfully'));
      onSuccess();
    }),

  addConcretePress: (payload, customerId, onSuccess) =>
    handleApiCallInStore(LoadingKeys.AddMaturityConcretePress, async () => {
      const { data } = await http.post<[[IBathPress], number]>(
        API_ENDPOINTS.maturity.addConcretePress,
        payload,
        { customerContextOverride: customerId } as any
      );
      // data[0][0] is the actual inserted row.
      // the backend return [results, metadata]
      // results its array of inserted rows. we insert only 1
      // in metadata we will get the value 1 represent the rows changes on DB
      const insertedPress = data?.[0]?.[0];

      if (!insertedPress || insertedPress.bath_temperature == null) {
        toast.error(t('serverError'));
        return;
      }

      const currentConcrete = get().selectedConcrete;
      if (currentConcrete) {
        set({
          selectedConcrete: {
            ...currentConcrete,
            concrete_bath_presses: [...currentConcrete.concrete_bath_presses, insertedPress],
          },
        });
      }

      toast.success(t('pressAddedSuccessfully'));
      onSuccess();
    }),

  calcMaturityParams: (concreteId, customerId, onSuccess) =>
    handleApiCallInStore(LoadingKeys.CalcMaturityParams, async () => {
      // unlike addConcrete/addConcretePress, this endpoint returns the updated
      // row directly (not a [rows, metadata] tuple) — see updateConcreteMaturityParams
      // in gv-new-backend's maturity.repository.ts.
      const { data: updatedConcrete } = await http.post<IMaturityConcrete>(
        API_ENDPOINTS.maturity.calcMaturityParams,
        { concrete_id: concreteId },
        { customerContextOverride: customerId } as any
      );

      if (!updatedConcrete || updatedConcrete.concrete_id == null) {
        toast.error(t('serverError'));
        return;
      }

      const currentConcrete = get().selectedConcrete;
      if (currentConcrete && currentConcrete.concrete_id === updatedConcrete.concrete_id) {
        set({ selectedConcrete: { ...currentConcrete, ...updatedConcrete } });
      }
      set({
        concretes: get().concretes.map((c) =>
          c.concrete_id === updatedConcrete.concrete_id ? { ...c, ...updatedConcrete } : c
        ),
      });

      toast.success(t('concreteCalibratedSuccessfully'));
      onSuccess();
    }),

  setSelectedConcrete: (concrete) => set({ selectedConcrete: concrete }),
}));
